export const pickFirstString = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
};

export const getUserFullName = (currentUser) => pickFirstString(
  currentUser?.full_name,
  currentUser?.fullName,
  currentUser?.fullname,
  currentUser?.name,
  currentUser?.display_name,
  currentUser?.displayName,
);

const normalizePersonKey = (value) => (
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
);

/**
 * Resolve the PAE Queue "PAE" filter value for the logged-in user.
 * Matches against partnerAdoptionEngineer options when available; otherwise
 * falls back to the user's display name so the default filter can apply early.
 */
export const resolveAssignedPaeFilterValue = (currentUser, paeOptions = []) => {
  if (!currentUser) {
    return null;
  }

  const fullName = getUserFullName(currentUser);
  const email = pickFirstString(
    currentUser?.email,
    currentUser?.partnerEmail,
    currentUser?.partner_email,
    currentUser?.user_email,
    currentUser?.username,
  );
  const emailLocal = email.includes('@')
    ? email.split('@')[0].replace(/[._]+/g, ' ').trim()
    : '';

  const candidates = [
    pickFirstString(currentUser?.partnerAdoptionEngineer, currentUser?.pae),
    fullName,
    emailLocal,
  ].filter(Boolean);

  const options = (paeOptions || [])
    .map((option) => String(option || '').trim())
    .filter(Boolean);

  const matchCandidateToOptions = (candidate) => {
    const normalizedCandidate = String(candidate || '').trim();
    if (!normalizedCandidate) {
      return null;
    }

    const lower = normalizedCandidate.toLowerCase();
    const exact = options.find((option) => option.toLowerCase() === lower);
    if (exact) {
      return exact;
    }

    const candidateKey = normalizePersonKey(normalizedCandidate);
    if (!candidateKey) {
      return null;
    }

    return options.find((option) => {
      const optionKey = normalizePersonKey(option);
      return optionKey
        && (optionKey === candidateKey
          || optionKey.includes(candidateKey)
          || candidateKey.includes(optionKey));
    }) || null;
  };

  for (const candidate of candidates) {
    const matched = matchCandidateToOptions(candidate);
    if (matched) {
      return matched;
    }
  }

  // Options not loaded yet — still default by display name so assigned projects show.
  if (!options.length && fullName) {
    return fullName;
  }

  // Options loaded but user is not in the PAE list (e.g. leadership/admin) — no default.
  return null;
};

export const getPartnerPrefillFromUser = (currentUser) => {
  const partnerName = pickFirstString(
    getUserFullName(currentUser),
    currentUser?.partnerName,
    currentUser?.organization,
    currentUser?.company,
  );

  const partnerEmail = pickFirstString(
    currentUser?.email,
    currentUser?.partnerEmail,
    currentUser?.partner_email,
    currentUser?.user_email,
    currentUser?.username,
  );

  const csn = pickFirstString(
    currentUser?.csn,
    currentUser?.CSN,
    currentUser?.customer_support_number,
    currentUser?.customerSupportNumber,
    currentUser?.partner_csn,
    currentUser?.partnerCsn,
  );

  return { partnerName, partnerEmail, csn };
};
